import React, { useEffect, useState } from "react";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { all_routes } from "../../../router/backendComponents/admin/all_routes";
import { isAuthenticated } from "./auth";
import { Navigate, Link, useNavigate } from "react-router-dom";
import apiRequestCall from "../../../apiHelper/globalHelper";
import { parseValidationErrors } from "../../../apiHelper/validationHelper";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { fetchProfile } from "../../../core/data/redux/profileDetails";
import type { RootState, AppDispatch } from "../../../core/data/redux/store";
import { useDispatch, useSelector } from "react-redux";

const Login = () => {
  // Get route list
  const route = all_routes?.backend;
  const MySwal = withReactContent(Swal);
  const showAlert1 = (title: any) => {
    MySwal.fire({
      title: title,
      confirmButtonColor: "#c21607",
      confirmButtonText: "OK",
    });
  };
  // Define state
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loader, setLoader] = useState(false);
  const [formError, setFormError] = useState<{
    email?: string;
    password?: string;
  }>({});
  const navigate = useNavigate();

  // Toggle password
  const togglePasswordVisibility = () => {
    setPasswordVisible((prevState) => !prevState);
  };

  // Get password
  const changeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Check for auth login
    if (isAuthenticated()) {
      return <Navigate to="/" replace />;
    }
    const { name, value } = e.target;
    if (name === "email") setEmail(value);
    else if (name === "password") setPassword(value);
    setFormError((prev) => {
      const updatedErrors = { ...prev };
      delete updatedErrors[name as keyof typeof prev];
      return updatedErrors;
    });
  };

  // Auth user
  const login = async (email: string, password: string) => {
    const result = await apiRequestCall("post", route["login"], {
      data: { email, password },
    });

    if (result?.data?.code === 200) {
      localStorage.setItem("authToken", result?.data?.data?.token);
    }
    return result?.data;
  };

  // Redirect user on basis of auth
  const handleLogin = async () => {
    setLoader(true);
    const res = await login(email, password);

    if (res?.code === 200) {
      setLoader(false);
      navigate("/");
      return;
    }

    if (res?.code === 221) {
      const parsed = parseValidationErrors(res?.errors);
      setLoader(false);
      setFormError(parsed);
      return;
    }

    // 🔑 Dynamic message handling
    if (!res || [401, 409, 422].includes(res?.code)) {
      setLoader(false);
      showAlert1(`
        <div class="alert alert-primary" role="alert">
          <strong>${res?.message ?? "Login failed"}</strong>
        </div>
      `);
      return;
    }
    setLoader(false);

    // Fallback
    showAlert1(`
      <div class="alert alert-danger" role="alert">
        Something went wrong. Please try again.
      </div>
    `);
  };

  // Use effect define
  useEffect(() => {
    localStorage.setItem("menuOpened", "Dashboard");
  }, []);

  return (
    <div className="account-content">
      <div className="d-flex flex-wrap w-100 vh-100">
        <div className="d-flex align-items-center justify-content-center flex-wrap vh-100 overflow-auto p-4 w-100 bg-backdrop">
          <form className="flex-fill">
            <div className="mx-auto mw-450">
              <div className="text-center mb-4">
                <ImageWithBasePath
                  src="assets/img/logo.png"
                  className="img-fluid"
                  alt="Logo"
                />
              </div>
              <div className="mb-4">
                <h4>Sign In</h4>
                <p>Access the admin panel using your email and passcode.</p>
              </div>
              <div className="mb-3">
                <label className="col-form-label">Email Address</label>
                <div className="position-relative">
                  <span className="input-icon-addon">
                    <i className="ti ti-mail"></i>
                  </span>
                  <input
                    type="text"
                    name="email"
                    value={email}
                    className="form-control"
                    onChange={changeInput}
                  />
                </div>
                {formError?.email && (
                  <div
                    className="invalid-feedback"
                    style={{
                      display: "block",
                      width: "100%",
                      marginTop: "0.25rem",
                      fontSize: "0.75rem",
                      color: "red",
                    }}
                  >
                    {formError.email}
                  </div>
                )}
              </div>
              <div className="mb-3">
                <label className="col-form-label">Password</label>
                <div className="pass-group">
                  <input
                    type={isPasswordVisible ? "text" : "password"}
                    className="pass-input form-control"
                    name="password"
                    value={password}
                    onChange={changeInput}
                  />
                  <span
                    className={`ti toggle-password ${
                      isPasswordVisible ? "ti-eye" : "ti-eye-off"
                    }`}
                    onClick={togglePasswordVisibility}
                  ></span>
                </div>

                {formError?.password && (
                  <div
                    className="invalid-feedback"
                    style={{
                      display: "block",
                      width: "100%",
                      marginTop: "0.25rem",
                      fontSize: "0.75rem",
                      color: "red",
                    }}
                  >
                    {formError.password}
                  </div>
                )}
              </div>

              {/* <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="form-check form-check-md d-flex align-items-center">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    id="checkebox-md"
                    defaultChecked
                  />
                  <label className="form-check-label" htmlFor="checkebox-md">
                    Remember Me
                  </label>
                </div>
                <div className="text-end">
                  <Link to={""} className="text-primary fw-medium link-hover">
                    Forgot Password?
                  </Link>
                </div>
              </div> */}
              <div className="mb-3">
                <button
                  className="btn btn-primary w-100"
                  onClick={handleLogin}
                  disabled={loader}
                >
                  {loader ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Signing in...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </button>
              </div>
              {/* <div className="mb-3">
                <h6>
                  New on our platform?
                  <Link to={""} className="text-purple link-hover">
                    {" "}
                    Create an account
                  </Link>
                </h6>
              </div> */}
              {/* <div className="form-set-login or-text mb-3">
                <h4>OR</h4>
              </div> */}
              <>
                {/* <div className="d-flex align-items-center justify-content-center flex-wrap mb-3">
                  <div className="text-center me-2 flex-fill">
                    <Link
                      to="#"
                      className="br-10 p-2 px-4 btn bg-pending  d-flex align-items-center justify-content-center"
                    >
                      <ImageWithBasePath
                        className="img-fluid m-1"
                        src="assets/img/icons/facebook-logo.png"
                        alt="Facebook"
                      />
                    </Link>
                  </div>
                  <div className="text-center me-2 flex-fill">
                    <Link
                      to="#"
                      className="br-10 p-2 px-4 btn bg-white d-flex align-items-center justify-content-center"
                    >
                      <ImageWithBasePath
                        className="img-fluid  m-1"
                        src="assets/img/icons/google-logo.png"
                        alt="Facebook"
                      />
                    </Link>
                  </div>
                  <div className="text-center flex-fill">
                    <Link
                      to="#"
                      className="bg-dark br-10 p-2 px-4 btn btn-dark d-flex align-items-center justify-content-center"
                    >
                      <ImageWithBasePath
                        className="img-fluid  m-1"
                        src="assets/img/icons/apple-logo.png"
                        alt="Apple"
                      />
                    </Link>
                  </div>
                </div> */}
                <div className="text-center">
                  <p className="fw-medium text-gray">
                    Copyright © 2025 - Cromatography
                  </p>
                </div>
              </>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
